import * as React from "react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * NewCardForm — inline "add a card" composer inside a ListColumn.
 * Submits on Enter, cancels on Escape or blur without content.
 */
export default function NewCardForm({ companyId, boardId, listId, onClose }) {
  const [title, setTitle] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const textareaRef = React.useRef(null);

  React.useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  async function submit() {
    const val = title.trim();
    if (!val || busy) return;
    setBusy(true);
    try {
      await api.post(`/companies/${companyId}/boards/${boardId}/lists/${listId}/cards`, { title: val });
      setTitle("");
      textareaRef.current?.focus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not add card");
    } finally {
      setBusy(false);
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    } else if (e.key === "Escape") {
      onClose && onClose();
    }
  }

  return (
    <div
      className="rounded-md bg-card border border-brand-rule p-2"
      data-testid={`list-new-card-form-${listId}`}
    >
      <textarea
        ref={textareaRef}
        className="field-input py-1.5 h-auto min-h-[54px] resize-none"
        placeholder="Card title. Enter to save, Esc to cancel."
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={onKeyDown}
        maxLength={200}
        data-testid={`list-new-card-input-${listId}`}
      />
      <div className="mt-1.5 flex items-center gap-2">
        <button
          type="button"
          onClick={submit}
          disabled={busy || !title.trim()}
          className="btn-violet h-8"
          data-testid={`list-new-card-submit-${listId}`}
        >
          {busy ? "…" : "Add card"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="btn-ghost h-8"
          data-testid={`list-new-card-cancel-${listId}`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
